import {Content, ContentAbsoluteContainer, SurveyStep} from "models"
import {FormInputs, useForm} from "@kaspernj/api-maker/build/form"
import React, {useMemo, useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AbsoluteContainer from "./absolute-container"
import AbsoluteContainerEditor from "./absolute-container-editor"
import AdminInput from "components/admin/input"
import Button from "components/inputs/button"
import classNames from "classnames"
import {digg} from "diggerize"
import EventEmitter from "events"
import Input from "components/inputs/input"
import memo from "set-state-compare/build/memo"
import Modal from "components/modal"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import RenderHtml from "shared/render-html"
import {Text} from "shared/base"
import TextKeysHandle from "components/text-keys/handle"
import TextKeysPartial from "components/text-keys/partial"
import TranslatedCollections from "@kaspernj/api-maker/build/translated-collections"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useQueryParams from "on-location-changed/build/use-query-params"
import useResizeObserver from "@kaspernj/api-maker/build/use-resize-observer"
import UtilsCode from "components/utils/code"
import UtilsSelect from "components/utils/select"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsContentsEditorProps
 * @property {boolean} absoluteContainers
 * @property {unknown} attributeName
 * @property {unknown} className
 * @property {unknown} inputProps
 * @property {unknown} name
 * @property {unknown} onChange
 * @property {boolean} preview
 * @property {boolean} reactNativePreview
 * @property {unknown} resource
 * @property {unknown} surveyStep
 */

/**
 * @typedef {object} ComponentsContentsEditorState
 * @property {unknown} absoluteContainers
 * @property {unknown} cacheKey
 * @property {unknown} content
 * @property {unknown} contentBodyType
 * @property {unknown} contentsEditorPreviewHeight
 * @property {unknown} contentsEditorPreviewWidth
 * @property {unknown} editAbsoluteContainer
 * @property {unknown} nestedAbsoluteContainers
 * @property {unknown} previewContentBody
 */

/**
 * @extends {ShapeComponent<ComponentsContentsEditorProps, ComponentsContentsEditorState>}
 */
class ComponentsContentsEditor extends ShapeComponent {
  /** @type {ComponentsContentsEditorState} */
  state = {
    absoluteContainers: undefined,
    cacheKey: undefined,
    contentBodyType: undefined,
    content: undefined,
    contentsEditorPreviewWidth: undefined,
    contentsEditorPreviewHeight: undefined,
    editAbsoluteContainer: undefined,
    nestedAbsoluteContainers: undefined,
    previewContentBody: undefined
  }

  static defaultProps = {
    absoluteContainers: true,
    preview: true,
    reactNativePreview: true
  }

  static propTypes = propTypesExact({
    absoluteContainers: PropTypes.bool.isRequired,
    attributeName: PropTypes.string.isRequired,
    inputProps: PropTypes.object,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    preview: PropTypes.bool.isRequired,
    reactNativePreview: PropTypes.bool.isRequired,
    resource: PropTypes.object.isRequired,
    surveyStep: PropTypes.instanceOf(SurveyStep)
  })

  containerEvents = new EventEmitter()
  initialContentToUse = this.initialContent()

  setup() {
    const {locale, t} = useI18n({namespace: "js.components.contents.editor"})

    this.bodyTypesCollection = useMemo(
      () => TranslatedCollections.get(Content, "body_type").map(({translation, value}) => ({text: translation, value})),
      [locale]
    )
    if (this.state.cacheKey === undefined) {
      this.state.cacheKey = this.initialContentToUse.fullCacheKey()
    }

    if (this.state.content === undefined) {
      this.state.content = this.initialContentToUse
    }

    if (this.state.contentBodyType === undefined) {
      this.state.contentBodyType = this.initialContentToUse.bodyType()
    }
    this.contentBodyType = this.initialContentToUse.bodyType()
    this.contentBodyValue = this.initialContentToUse.body() || ""

    if (this.state.previewContentBody === undefined) {
      this.state.previewContentBody = this.contentBodyValue
    }

    this.form = useForm() || new FormInputs()
    this.previewContentRef = useRef()
    this.queryParams = useQueryParams()
    this.t = t

    useResizeObserver(this.tt.previewContentRef.current, this.tt.onPreviewContentResize)

    useMemo(() => {
      this.loadAbsoluteContainersNestedSet()
      this.setContentsEditorPreviewSize()
    }, [])
  }

  componentDidMount() {
    this.syncFormValues()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.attributeName != this.props.attributeName || prevProps.name != this.props.name) {
      this.syncFormValues()

      return
    }

    if (prevState.content != this.s.content) {
      this.contentBodyType = this.s.content.bodyType()
      this.tt.contentBodyValue = this.s.content.body() || ""
      this.setState({contentBodyType: this.contentBodyType, previewContentBody: this.tt.contentBodyValue}, this.syncFormValues)
    }
  }

  initialAbsoluteContainers() {
    const {attributeName, resource} = this.p

    if (resource.isPersisted()) {
      const existingContent = resource.contents().loaded().find((content) => content.attributeName() == attributeName)

      if (existingContent) {
        return existingContent.absoluteContainers().loaded()
      }
    }

    return []
  }

  async loadAbsoluteContainersNestedSet() {
    const absoluteContainerIds = this.initialAbsoluteContainers().map((absoluteContainer) => absoluteContainer.id())
    const query = ContentAbsoluteContainer
      .ransack()
      .preload(["styling", "survey_step_element", "text_keys.text_values"])
      .select({
        ContentAbsoluteContainer: [
          "backgroundImageUrl",
          "body",
          "bodyType",
          "heightPercent",
          "heightPixels",
          "horizontalAlign",
          "id",
          "leftPercent",
          "leftPixels",
          "liquidConditions",
          "minimumHeight",
          "minimumWidth",
          "parentId",
          "responsive",
          "stylingId",
          "topPercent",
          "topPixels",
          "verticalAlign",
          "widthPercent",
          "widthPixels"
        ],
        Styling: ["id", "styling"],
        SurveyStepElement: ["elementType", "id", "translatedElementType"],
        TextKey: ["attributeName", "id", "key"],
        TextValue: ["id", "locale", "value", "valueType"]
      })
    const nestedAbsoluteContainersResult = await ContentAbsoluteContainer.nestedSet({absolute_container_ids: absoluteContainerIds, query})
    const nestedAbsoluteContainersList = digg(nestedAbsoluteContainersResult, "collection")
    const nestedAbsoluteContainers = {}

    for (const nestedAbsoluteContainer of nestedAbsoluteContainersList) {
      const parentId = nestedAbsoluteContainer.parentId()

      if (!(parentId in nestedAbsoluteContainers)) nestedAbsoluteContainers[parentId] = []

      nestedAbsoluteContainers[parentId].push(nestedAbsoluteContainer)
    }

    let rootAbsoluteContainers

    if (nestedAbsoluteContainers[null]) {
      rootAbsoluteContainers = [...nestedAbsoluteContainers[null]]
    } else {
      rootAbsoluteContainers = []
    }

    this.setState({
      absoluteContainers: rootAbsoluteContainers,
      nestedAbsoluteContainers
    })
  }

  initialContent() {
    const {attributeName, resource} = this.p

    if (resource.isPersisted()) {
      const existingContent = resource.contents().loaded().find((content) => content.attributeName() == attributeName)

      if (existingContent) return existingContent
    }

    return new Content({
      a: {
        body: null,
        body_type: "plain",
        id: uuidv4()
      },
      isNewRecord: true
    })
  }


  render() {
    const {bodyTypesCollection, form, queryParams} = this.tt
    const {className, inputProps} = this.props
    const {attributeName, name, resource} = this.p
    const {
      absoluteContainers,
      cacheKey,
      contentsEditorPreviewWidth,
      contentsEditorPreviewHeight,
      content,
      editAbsoluteContainer,
      nestedAbsoluteContainers,
      previewContentBody
    } = this.s
    const contentBody = this.tt.contentBodyValue || ""
    const contentBodyType = this.s.contentBodyType
    const contentBodyTypeValues = this.cache("contentBodyTypeValues", [contentBodyType], [contentBodyType])
    const previewClassNames = [
      "preview-content",
      "ck-content" // Needs to apply CKEditor specific styling to make it look right
    ]
    let absoluteContainerName

    if (name) {
      absoluteContainerName = `${name}[absolute_containers_attributes]`
    } else {
      absoluteContainerName = "absolute_containers_attributes"
    }

    const liquidVariables = content.detectedLiquidVariables() || queryParams.detected_liquid_variables

    return (
      <View dataSet={this.cache("contentsEditorDataSet", {component: "contents/editor", class: className}, [className])}>
        {this.p.absoluteContainers && editAbsoluteContainer &&
          <Modal onRequestClose={this.tt.onRequestCloseEditAbsoluteContainerModal}>
            <AbsoluteContainerEditor
              absoluteContainer={editAbsoluteContainer}
              events={this.tt.containerEvents}
              onAbsoluteContainerChanged={this.tt.onAbsoluteContainerChanged}
              onRequestClose={this.tt.onAbsoluteContainerEditorRequestClose}
              surveyStep={this.props.surveyStep}
            />
          </Modal>
        }
        {liquidVariables &&
          <>
            <Text>{Content.humanAttributeName("detectedLiquidVariables")}</Text>
            <UtilsCode
              viewProps={this.liquidVariablesViewProps ||= {
                dataSet: this.liquidVariablesDataSet ||= {class: "liquid-variables-container"},
                style: this.liquidVariablesStyle ||= {marginBottom: 10}
              }}
            >
              {JSON.stringify(liquidVariables, null, 2)}
            </UtilsCode>
          </>
        }
        <View style={this.contentBodyTypeContainerStyle ||= {marginBottom: 10}}>
          <UtilsSelect
            defaultValues={contentBodyTypeValues}
            id="content_body_type"
            label={Content.humanAttributeName("bodyType")}
            name={`${name}[body_type]`}
            onChangeSelected={this.tt.onContentBodyTypeChanged}
            options={bodyTypesCollection}
          />
        </View>
        {form.setValueWithHidden(`${name}[attribute_name]`, attributeName)}
        {content.isPersisted() &&
          form.setValueWithHidden(`${name}[id]`, content.id())
        }
        {content.isNewRecord() &&
          form.setValueWithHidden(`${name}[new_id]`, content.id())
        }
        {contentBodyType == "ckeditor" &&
          <Input
            defaultValue={contentBody}
            id={this.inputId()}
            model={resource}
            name={`${name}[body]`}
            onChange={this.tt.onBodyChanged}
            type="ckeditor"
            {...inputProps}
          />
        }
        {contentBodyType == "plain" &&
          <AdminInput
            autoRows
            dataSet={this.contentBodyDataSet ||= {class: "content-body"}}
            defaultValue={contentBody}
            label={Content.humanAttributeName("body")}
            minimumRows={10}
            multiline
            name={`${name}[body]`}
            onChangeText={this.tt.onContentBodyChanged}
          />
        }
        {this.p.preview &&
          <div
            className="contents-editor-preview"
            data-class="components--ckeditor--presentation"
            data-testid="contents-editor-preview"
            id="contents-editor-preview"
            ref={this.tt.previewContentRef}
          >
            {this.p.absoluteContainers &&
            contentsEditorPreviewWidth &&
            contentsEditorPreviewHeight &&
            nestedAbsoluteContainers &&
            absoluteContainers?.map((absoluteContainer) =>
              <AbsoluteContainer
                absoluteContainer={absoluteContainer}
                cacheKey={`${cacheKey}-${absoluteContainer.localCacheKey()}`}
                events={this.tt.containerEvents}
                key={absoluteContainer.uniqueKey()}
                name={`${absoluteContainerName}[${absoluteContainer.uniqueKey()}]`}
                nestedAbsoluteContainers={nestedAbsoluteContainers}
                onDoubleClick={this.tt.onAbsoluteContainerPreviewClicked}
                parentHeight={contentsEditorPreviewHeight}
                parentWidth={contentsEditorPreviewWidth}
                surveyStep={this.props.surveyStep}
              />
            )}
            {this.p.reactNativePreview &&
              <View testID="contents-editor-react-native-preview">
                <RenderHtml source={previewContentBody} />
              </View>
            }
            {!this.p.reactNativePreview &&
              <div
                className={classNames(previewClassNames)}
                dangerouslySetInnerHTML={{__html: contentBody}}
                data-testid="contents-editor-browser-html-preview"
              />
            }
          </div>
        }
        {this.p.absoluteContainers && absoluteContainers &&
          <Button
            className="add-absolute-container-button"
            icon="plus"
            label={this.t(".add_absolute_container")}
            onClick={this.tt.onAddAbsoluteContainerClicked}
          />
        }
        <TextKeysHandle cacheKey={cacheKey} name={name} resource={content} />
        <TextKeysPartial
          attributeName="body"
          cacheKey={cacheKey}
          contentGetter={this.tt.contentBodyValueGetter}
          name={name}
          onTextKeysChanged={this.tt.onTextKeysChanged}
          resource={content}
        />
      </View>
    )
  }

  inputId = () => this.props.inputProps?.id || "contents_editor_input"
  onAbsoluteContainerPreviewClicked = (e, absoluteContainer) => {
    e.stopPropagation()

    this.setState({editAbsoluteContainer: absoluteContainer})
  }

  onAddAbsoluteContainerClicked = (e) => {
    e.preventDefault()

    const contentsEditorPreview = digg(this.tt.previewContentRef, "current")
    const contentsEditorPreviewWidth = digg(contentsEditorPreview, "offsetWidth")
    const contentsEditorPreviewHeight = digg(contentsEditorPreview, "offsetHeight")

    const widthPixels = Number(contentsEditorPreviewWidth / 3)
    const heightPixels = Number(contentsEditorPreviewHeight / 3)
    const leftPixels = Number(contentsEditorPreviewWidth * 0.02)
    const topPixels = Number(contentsEditorPreviewHeight * 0.02)
    const heightPercent = (heightPixels / contentsEditorPreviewHeight) * 100
    const leftPercent = (leftPixels / contentsEditorPreviewWidth) * 100
    const topPercent = (topPixels / contentsEditorPreviewHeight) * 100
    const widthPercent = (widthPixels / contentsEditorPreviewWidth) * 100

    const newAbsoluteContainer = new ContentAbsoluteContainer({
      a: {
        body: "",
        bodyType: "plain",
        id: uuidv4(),
        heightPercent,
        heightPixels,
        leftPercent,
        leftPixels,
        topPercent,
        topPixels,
        widthPercent,
        widthPixels
      },
      isNewRecord: true
    })

    this.setState({
      absoluteContainers: this.s.absoluteContainers.concat([newAbsoluteContainer])
    })
  }

  onAbsoluteContainerChanged = () => this.setState({cacheKey: this.s.content.fullCacheKey()})
  onAbsoluteContainerEditorRequestClose = () => this.setState({editAbsoluteContainer: undefined})
  onBodyChanged = ({value}) => this.onContentBodyChanged(value)
  contentBodyValueGetter = () => this.tt.contentBodyValue || ""
  syncFormValues = () => {
    const {form} = this.tt

    if (!form) return null

    const {attributeName, name} = this.props
    const {content, contentBodyType} = this.s
    const contentBodyValue = this.tt.contentBodyValue || ""

    form.setValue(`${name}[attribute_name]`, attributeName)

    if (content?.isPersisted?.()) {
      form.setValue(`${name}[id]`, content.id())
    } else if (content?.isNewRecord?.()) {
      form.setValue(`${name}[new_id]`, content.id())
    }

    form.setValue(`${name}[body]`, contentBodyValue)
    form.setValue(`${name}[body_type]`, contentBodyType)

    return null
  }

  onContentBodyChanged = (value) => {
    const {form} = this.tt

    this.tt.contentBodyValue = value
    this.tt.updatePreviewBody(value)

    if (form) {
      form.setValue(`${this.props.name}[body]`, value)
    }

    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  onContentBodyTypeChanged = (contentBodyType) => {
    const {form} = this.tt

    this.tt.contentBodyType = contentBodyType

    if (contentBodyType != this.s.contentBodyType) {
      this.setState({contentBodyType})
    }

    if (form) {
      form.setValue(`${this.props.name}[body_type]`, contentBodyType)
    }
  }

  onPreviewContentResize = () => this.tt.setContentsEditorPreviewSize()
  onRequestCloseEditAbsoluteContainerModal = () => this.setState({editAbsoluteContainer: undefined})
  onTextKeysChanged = () => this.setState({cacheKey: this.s.content.fullCacheKey()})

  updatePreviewBody = (value) => {
    if (this.p.reactNativePreview) {
      this.setState({previewContentBody: value})

      return
    }

    const previewElement = this.tt.previewContentRef.current?.querySelector?.(".preview-content")

    if (previewElement) previewElement.innerHTML = value || ""
  }

  setContentsEditorPreviewSize = () => {
    const contentsEditorPreview = digg(this.tt.previewContentRef, "current")
    const width = contentsEditorPreview?.offsetWidth
    const height = contentsEditorPreview?.offsetHeight

    this.setState({
      contentsEditorPreviewWidth: width,
      contentsEditorPreviewHeight: height
    })
  }
}

export default memo(shapeComponent(ComponentsContentsEditor))
